import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  GripVertical,
  AlertTriangle,
  ImageIcon,
  Languages,
} from 'lucide-react';

const apiFetch = (path, init = {}) =>
  fetch(path, { credentials: 'same-origin', ...init });

const emptyBi = () => ({ en: '', de: '' });

const blankProject = () => ({
  id: '',
  year: String(new Date().getFullYear()),
  month: '',
  tags: [],
  title: emptyBi(),
  summary: emptyBi(),
  thumbnail: '',
  sections: [{ key: emptyBi(), value: emptyBi() }],
  media: [],
  hidden: false,
});

// Convert any (mock or kv) DisplayProject into the form's editable shape.
function fromDisplay(p) {
  if (!p) return blankProject();
  return {
    id: '', // always empty for clones — the user picks a new id by retyping the title
    year: p.year || '',
    month: p.month || '',
    tags: Array.isArray(p.tags) ? [...p.tags] : [],
    title: { en: p.title?.en || '', de: p.title?.de || '' },
    summary: { en: p.summary?.en || '', de: p.summary?.de || '' },
    thumbnail: p.thumbnail || '',
    sections: Array.isArray(p.sections) && p.sections.length
      ? p.sections.map((s) => ({
          key: { en: s.key?.en || '', de: s.key?.de || '' },
          value: { en: s.value?.en || '', de: s.value?.de || '' },
        }))
      : [{ key: emptyBi(), value: emptyBi() }],
    media: Array.isArray(p.media)
      ? p.media.map((m) => ({
          url: m.src || m.url || '',
          caption: { en: m.caption?.en || '', de: m.caption?.de || '' },
        }))
      : [],
    hidden: false,
  };
}

const BiField = ({ label, value, onChange, multiline, required }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 text-xs mono uppercase tracking-widest text-slate-500">
      <Languages className="w-3.5 h-3.5" />
      <span>{label}{required ? ' *' : ''}</span>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {['en', 'de'].map((lang) => (
        <div key={lang}>
          <span className="block text-[10px] mono uppercase tracking-wider text-slate-500 mb-1">
            {lang.toUpperCase()}
          </span>
          {multiline ? (
            <Textarea
              value={value?.[lang] || ''}
              onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
              className="bg-[#10151F] border-white/10 text-slate-100 min-h-[100px]"
              data-testid={`bi-${label.toLowerCase().replace(/\s+/g, '-')}-${lang}`}
            />
          ) : (
            <Input
              value={value?.[lang] || ''}
              onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
              className="bg-[#10151F] border-white/10 text-slate-100"
              data-testid={`bi-${label.toLowerCase().replace(/\s+/g, '-')}-${lang}`}
            />
          )}
        </div>
      ))}
    </div>
  </div>
);

const AdminProjectForm = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const { state } = useLocation();
  const isEdit = !!editId;
  const cloneFrom = state?.cloneFrom;

  const [form, setForm] = useState(() =>
    cloneFrom ? fromDisplay(cloneFrom) : blankProject(),
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tagInput, setTagInput] = useState('');

  // Load existing project for edit mode.
  useEffect(() => {
    if (!isEdit) return;
    let alive = true;
    setLoading(true);
    apiFetch('/api/admin/projects')
      .then(async (r) => {
        const ct = r.headers.get('content-type') || '';
        if (r.status === 401 || r.status === 403 || !ct.includes('application/json')) {
          if (alive) setError('unauthorized');
          return null;
        }
        const j = await r.json();
        return j;
      })
      .then((j) => {
        if (!alive || !j?.ok) return;
        const target = (j.projects || []).find((p) => p.id === editId);
        if (target) {
          setForm({
            id: target.id,
            year: target.year || '',
            month: target.month || '',
            tags: Array.isArray(target.tags) ? [...target.tags] : [],
            title: target.title || emptyBi(),
            summary: target.summary || emptyBi(),
            thumbnail: target.thumbnail || '',
            sections: Array.isArray(target.sections) && target.sections.length
              ? target.sections
              : [{ key: emptyBi(), value: emptyBi() }],
            media: Array.isArray(target.media) ? target.media : [],
            hidden: !!target.hidden,
          });
        } else {
          setError('not-found');
        }
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [editId, isEdit]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Sections
  const updateSection = (idx, patch) => {
    setForm((f) => {
      const next = [...f.sections];
      next[idx] = { ...next[idx], ...patch };
      return { ...f, sections: next };
    });
  };
  const addSection = () =>
    setForm((f) => ({
      ...f,
      sections: [...f.sections, { key: emptyBi(), value: emptyBi() }],
    }));
  const removeSection = (idx) =>
    setForm((f) => ({
      ...f,
      sections: f.sections.filter((_, i) => i !== idx),
    }));
  const moveSection = (idx, dir) =>
    setForm((f) => {
      const next = [...f.sections];
      const tgt = idx + dir;
      if (tgt < 0 || tgt >= next.length) return f;
      [next[idx], next[tgt]] = [next[tgt], next[idx]];
      return { ...f, sections: next };
    });

  // Media
  const updateMedia = (idx, patch) => {
    setForm((f) => {
      const next = [...f.media];
      next[idx] = { ...next[idx], ...patch };
      return { ...f, media: next };
    });
  };
  const addMedia = () =>
    setForm((f) => ({
      ...f,
      media: [...f.media, { url: '', caption: emptyBi() }],
    }));
  const removeMedia = (idx) =>
    setForm((f) => ({ ...f, media: f.media.filter((_, i) => i !== idx) }));

  // Tags
  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!form.tags.includes(t)) set({ tags: [...form.tags, t] });
    setTagInput('');
  };
  const removeTag = (t) => set({ tags: form.tags.filter((x) => x !== t) });

  const canSave = useMemo(() => {
    return (form.title.en || form.title.de) && form.year;
  }, [form]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSave) {
      toast.error('Title (DE or EN) and Year are required.');
      return;
    }
    setSaving(true);
    try {
      const url = isEdit
        ? `/api/admin/projects/${encodeURIComponent(editId)}`
        : '/api/admin/projects';
      const res = await apiFetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok || !ct.includes('application/json')) {
        throw new Error(`HTTP ${res.status}`);
      }
      const j = await res.json();
      if (!j.ok) throw new Error(j.error || 'Save failed');
      toast.success(isEdit ? 'Project updated' : 'Project created');
      navigate('/admin/projects');
    } catch (err) {
      toast.error(`Failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (error === 'unauthorized') {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center">
        <AlertTriangle className="w-10 h-10 text-[#D95871] mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-50">Access required</h1>
      </div>
    );
  }

  return (
    <div>
      <section className="border-b border-white/10 sky-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-10">
          <Link
            to="/admin/projects"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#4A8FD1] mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to projects
          </Link>
          <h1 className="text-3xl font-bold text-slate-50">
            {isEdit ? 'Edit project' : cloneFrom ? 'Clone project' : 'New project'}
          </h1>
          {cloneFrom && !isEdit && (
            <p className="mt-2 text-sm text-slate-400">
              Cloning from "{cloneFrom.title.en || cloneFrom.title.de}". Edit anything below; saving creates a brand-new project.
            </p>
          )}
        </div>
      </section>

      <form onSubmit={submit} className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-10 space-y-10">
        {loading && (
          <div className="p-4 rounded-md border border-white/10 bg-[#1A2231] text-slate-400">
            Loading project…
          </div>
        )}

        {/* Meta */}
        <fieldset className="space-y-5 p-6 rounded-lg border border-white/10 bg-[#1A2231]">
          <legend className="px-2 text-xs mono uppercase tracking-widest text-[#4A8FD1]">
            Project meta
          </legend>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs mono uppercase tracking-widest text-slate-500 mb-1.5">Year *</label>
              <Input
                value={form.year}
                onChange={(e) => set({ year: e.target.value })}
                placeholder="2017"
                className="bg-[#10151F] border-white/10 text-slate-100"
                data-testid="project-year-input"
              />
            </div>
            <div>
              <label className="block text-xs mono uppercase tracking-widest text-slate-500 mb-1.5">Month</label>
              <Input
                value={form.month}
                onChange={(e) => set({ month: e.target.value })}
                placeholder="03 (optional)"
                className="bg-[#10151F] border-white/10 text-slate-100"
              />
            </div>
            <div>
              <label className="block text-xs mono uppercase tracking-widest text-slate-500 mb-1.5">Slug (optional)</label>
              <Input
                value={form.id}
                onChange={(e) => set({ id: e.target.value })}
                placeholder="auto from title"
                disabled={isEdit}
                className="bg-[#10151F] border-white/10 text-slate-100"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs mono uppercase tracking-widest text-slate-500 mb-1.5">Tags</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 border border-white/10 text-xs text-slate-200"
                >
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} className="text-slate-400 hover:text-[#D95871]">
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag();
                  }
                }}
                placeholder="Add tag and press Enter"
                className="bg-[#10151F] border-white/10 text-slate-100"
                data-testid="project-tag-input"
              />
              <Button type="button" onClick={addTag} variant="outline" className="border-white/15 bg-transparent text-slate-200">
                Add
              </Button>
            </div>
          </div>

          <BiField label="Title" required value={form.title} onChange={(v) => set({ title: v })} />
          <BiField label="Summary" multiline value={form.summary} onChange={(v) => set({ summary: v })} />

          <div>
            <label className="block text-xs mono uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-2">
              <ImageIcon className="w-3.5 h-3.5" />
              Thumbnail (Cloudinary URL)
            </label>
            <Input
              value={form.thumbnail}
              onChange={(e) => set({ thumbnail: e.target.value })}
              placeholder="https://res.cloudinary.com/dwoy6uzfu/image/upload/..."
              className="bg-[#10151F] border-white/10 text-slate-100"
              data-testid="project-thumbnail-input"
            />
            {form.thumbnail && (
              <div className="mt-3 w-48 aspect-[16/10] rounded overflow-hidden bg-white/5 border border-white/10">
                <img src={form.thumbnail} alt="" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
        </fieldset>

        {/* Sections */}
        <fieldset className="space-y-4 p-6 rounded-lg border border-white/10 bg-[#1A2231]">
          <legend className="px-2 text-xs mono uppercase tracking-widest text-[#4BADC8]">
            Sections (rendered in order)
          </legend>

          {form.sections.map((s, idx) => (
            <div key={idx} className="p-4 rounded-md border border-white/10 bg-[#10151F] space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="mono text-xs uppercase tracking-wider text-slate-500">
                  Section {String(idx + 1).padStart(2, '0')}
                </span>
                <div className="flex items-center gap-1">
                  <Button type="button" size="sm" variant="ghost" className="text-slate-400 hover:text-slate-100" onClick={() => moveSection(idx, -1)} disabled={idx === 0}>↑</Button>
                  <Button type="button" size="sm" variant="ghost" className="text-slate-400 hover:text-slate-100" onClick={() => moveSection(idx, 1)} disabled={idx === form.sections.length - 1}>↓</Button>
                  <Button type="button" size="sm" variant="ghost" className="text-[#FFB3BF] hover:bg-[#D95871]/10" onClick={() => removeSection(idx)} disabled={form.sections.length === 1}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <BiField label="Section title" value={s.key} onChange={(v) => updateSection(idx, { key: v })} />
              <BiField label="Section content" multiline value={s.value} onChange={(v) => updateSection(idx, { value: v })} />
            </div>
          ))}

          <Button type="button" variant="outline" className="border-white/15 bg-transparent text-slate-200" onClick={addSection}>
            <Plus className="w-4 h-4 mr-2" />
            Add section
          </Button>
        </fieldset>

        {/* Media */}
        <fieldset className="space-y-4 p-6 rounded-lg border border-white/10 bg-[#1A2231]">
          <legend className="px-2 text-xs mono uppercase tracking-widest text-[#E4A135]">
            Media (Cloudinary)
          </legend>
          <p className="text-xs text-slate-500">
            Type (image / video) is auto-detected from the URL — Cloudinary's <code className="mono">/image/upload/</code> vs <code className="mono">/video/upload/</code> path.
          </p>

          {form.media.map((m, idx) => (
            <div key={idx} className="p-4 rounded-md border border-white/10 bg-[#10151F] space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="mono text-xs uppercase tracking-wider text-slate-500">
                  Item {String(idx + 1).padStart(2, '0')}
                </span>
                <Button type="button" size="sm" variant="ghost" className="text-[#FFB3BF] hover:bg-[#D95871]/10" onClick={() => removeMedia(idx)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <Input
                value={m.url}
                onChange={(e) => updateMedia(idx, { url: e.target.value })}
                placeholder="https://res.cloudinary.com/.../upload/..."
                className="bg-[#1A2231] border-white/10 text-slate-100"
              />
              <BiField
                label="Caption (optional)"
                value={m.caption || emptyBi()}
                onChange={(v) => updateMedia(idx, { caption: v })}
              />
              {m.url && (
                <div className="w-48 aspect-[16/10] rounded overflow-hidden bg-white/5 border border-white/10">
                  {m.url.toLowerCase().includes('/video/upload/') ? (
                    <video src={m.url} muted className="w-full h-full object-contain bg-black" preload="metadata" />
                  ) : (
                    <img src={m.url} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
              )}
            </div>
          ))}

          <Button type="button" variant="outline" className="border-white/15 bg-transparent text-slate-200" onClick={addMedia}>
            <Plus className="w-4 h-4 mr-2" />
            Add media item
          </Button>
        </fieldset>

        {/* Submit */}
        <div className="flex items-center justify-end gap-3 sticky bottom-0 -mx-5 sm:-mx-8 lg:-mx-12 px-5 sm:px-8 lg:px-12 py-4 bg-[#0B121E]/95 backdrop-blur border-t border-white/10">
          <Link
            to="/admin/projects"
            className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200"
          >
            Cancel
          </Link>
          <Button
            type="submit"
            disabled={saving || !canSave}
            className="bg-[#4A8FD1] hover:bg-[#6AAFE0] text-slate-50"
            data-testid="project-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving…' : isEdit ? 'Update project' : 'Create project'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminProjectForm;
